import { RecordingSelectionSummary } from "../components/RecordingSelectionSummary";
import { recordingLabel } from "../recordingLabel";
import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { importOperationalReport, listOperationalRecordings, operationalKeys } from "@rekonas/data-access";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { OperatorPanel } from "../components/OperatorPanel";
import { useOperationalWorkspace } from "../operationalWorkspace";
import { operationalRecordingPath } from "./RecordingsPage";

/** Intentionally unlisted manual producer. Authentication is provided by App. */
export function ReportImportPage() {
  const access = useOperationalWorkspace();
  const [chosenWorkspace, setChosenWorkspace] = useState("");
  const [chosenRecording, setChosenRecording] = useState("");
  const [pdf, setPdf] = useState<File | null>(null);
  const [json, setJson] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ report_ref: string } | null>(null);
  const [error, setError] = useState("");
  const destinations = access.workspaces.filter((item) => item.permissions.includes("recordings.upload"));
  const workspaceRef = destinations.some((item) => item.slug === chosenWorkspace)
    ? chosenWorkspace : destinations[0]?.slug || "";
  const recordings = useQuery({
    queryKey: operationalKeys.list(workspaceRef),
    queryFn: ({ signal }) => listOperationalRecordings(workspaceRef, signal),
    enabled: Boolean(workspaceRef),
  });
  const items = recordings.data?.items || [];
  const recordingRef = items.some((item) => item.recording_ref === chosenRecording)
    ? chosenRecording : items[0]?.recording_ref || "";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!recordingRef || !pdf || !json || busy) return;
    setBusy(true); setError(""); setResult(null);
    try {
      setResult(await importOperationalReport(recordingRef, pdf, json));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Report import failed.");
    } finally {
      setBusy(false);
    }
  }

  if (access.isPending) return <main className="operational-loading" role="status"><Spinner /> Loading authorized workspaces…</main>;
  return <main className="operational-main operator-main">
    <Link className="operational-back" to="/recordings">← All recordings</Link>
    <header className="recording-detail-header operator-header"><div>
      <p>Internal · Temporary operator tool</p><h1>Internal report import</h1>
      <span>Attach one externally prepared PDF and one JSON document to an Operational recording.</span>
    </div></header>
    <p className="operator-muted">Temporary manual report-import path for operators. Select a recording and both files, then validate and import the bundle.</p>
    {!destinations.length ? <Alert>No current upload permission is available for this account.</Alert> :
      <form className="operator-form" onSubmit={(event) => void submit(event)}>
        <OperatorPanel title="1. Select recording" description="Choose the destination for this report bundle.">
          <div className="operator-fields">
            <label>Workspace <select aria-label="Report workspace" value={workspaceRef} onChange={(event) => {
              setChosenWorkspace(event.target.value); setChosenRecording(""); setResult(null);
            }}>{destinations.map((item) => <option key={item.slug} value={item.slug}>{item.display_name}</option>)}</select></label>
            {recordings.isPending ? <p className="operator-loading" role="status"><Spinner /> Loading recordings…</p> : recordings.isError ? <Alert>Could not load recordings.</Alert> :
              <label>Recording <select aria-label="Report recording" value={recordingRef} onChange={(event) => {
                setChosenRecording(event.target.value); setResult(null);
              }}>{items.map((item) => <option key={item.recording_ref} value={item.recording_ref}>
                {recordingLabel(item)}</option>)}</select></label>}
          </div>
          <RecordingSelectionSummary recording={items.find(item => item.recording_ref === recordingRef)} />
          {!recordings.isPending && !recordings.isError && !items.length && <p className="operator-muted">No authorized recordings are available in this workspace.</p>}
        </OperatorPanel>
        <div className="operator-fields operator-file-panels">
          <OperatorPanel title="2. Choose PDF" description="Select the externally prepared report PDF.">
            <label className="operator-field">PDF <input aria-label="Report PDF" aria-describedby="report-pdf-feedback" type="file" accept="application/pdf,.pdf" onChange={(event) => setPdf(event.target.files?.[0] || null)} /></label>
            <p id="report-pdf-feedback" className="operator-file-feedback">{pdf ? `${pdf.name} · ${pdf.size.toLocaleString()} bytes` : "No PDF selected."}</p>
          </OperatorPanel>
          <OperatorPanel title="3. Choose JSON" description="Select the JSON document for this report bundle.">
            <label className="operator-field">JSON <input aria-label="Report JSON file" aria-describedby="report-json-feedback" type="file" accept="application/json,.json" onChange={(event) => setJson(event.target.files?.[0] || null)} /></label>
            <p id="report-json-feedback" className="operator-file-feedback">{json ? `${json.name} · ${json.size.toLocaleString()} bytes` : "No JSON selected."}</p>
          </OperatorPanel>
        </div>
        <OperatorPanel title="4. Validate and import" description="The import validates the bundle. Both files and a recording are required.">
          {error && <Alert role="alert">{error}</Alert>}
          <div className="operator-actions"><Button type="submit" disabled={!recordingRef || !pdf || !json || busy}>
            {busy ? "Importing…" : "Import report bundle"}
          </Button>{busy && <span className="operator-muted">Validating and importing the report bundle…</span>}</div>
        </OperatorPanel>
      </form>}
    <OperatorPanel title="5. Import result" current={Boolean(result)}>
      {result ? <div className="operator-success" role="status"><strong>Report {result.report_ref} imported.</strong>
        <p>Open the recording viewer to review the current report state.</p>
        <Link className="operational-back" to={operationalRecordingPath(workspaceRef, recordingRef)}>Open recording viewer →</Link>
      </div> : <p className="operator-muted">No import result yet. The recording viewer shows the current report state.</p>}
    </OperatorPanel>
  </main>;
}
